// Thin platform abstraction: on the web/PWA this is a no-op (pure feature detection, no native
// deps touched); inside the Capacitor iOS/macOS shell it saves through the native Filesystem and
// opens the native share sheet (which itself offers "Save Image"/"Save to Photos" for images and
// "Save to Files" for anything else) instead of a browser <a download> click.

export function isNativePlatform(): boolean {
  return typeof (globalThis as any).Capacitor !== 'undefined' && (globalThis as any).Capacitor.isNativePlatform?.() === true;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

/**
 * Saves a file on-device and opens the native share sheet so the user can pick "Save to Photos",
 * "Save to Files", AirDrop, or hand it to another app ("Open in…"). Falls back to a plain browser
 * download when not running inside the native shell.
 */
export async function saveAndShare(blob: Blob, filename: string): Promise<void> {
  if (!isNativePlatform()) {
    browserDownload(blob, filename);
    return;
  }

  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { Share } = await import('@capacitor/share');

  const base64 = await blobToBase64(blob);
  const result = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });

  await Share.share({
    title: filename,
    url: result.uri,
  });
}

export function browserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
